/**
 * Optional-field UX for shared outline form controls:
 * - Placeholders are **value hints** like required fields (e.g. “Enter name”), never the word
 *   “Optional”.
 * - `[optional]="true"` adds ` (optional)` to the label and clears the required affordance only.
 *
 * Matches labels that already mark optional, so we do not append a second suffix.
 */
const OPTIONAL_MARKED_IN_LABEL = /\(optional\)/i;

/**
 * Appends ` (optional)` to the label when {@link optional} is true, unless the label already
 * contains `(optional)` (any casing).
 */
export function withOptionalLabelSuffix(
  optional: boolean,
  label: string | undefined,
): string | undefined {
  if (!label?.trim()) {
    return label;
  }
  if (!optional) {
    return label;
  }
  if (OPTIONAL_MARKED_IN_LABEL.test(label)) {
    return label;
  }
  return `${label} (optional)`;
}

/** Passes through an explicit placeholder only; callers supply entry hints, not “Optional”. */
export function explicitPlaceholder(placeholder: string | undefined | null): string | undefined {
  if (placeholder !== undefined && placeholder !== null) {
    return placeholder;
  }
  return undefined;
}

/** `required` is not advertised on the control when {@link optional} is true. */
export function effectiveOptionalRequired(required: boolean, optional: boolean): boolean {
  return Boolean(required) && !optional;
}

/**
 * When there is a floating {@link label}, optional state is shown only on that label (returns
 * `undefined` here so `aria-label` is not duplicated for screen readers). When there is no label
 * but an {@link ariaLabel} is provided, optional is reflected on `aria-label`.
 */
export function withOptionalAriaLabel(
  optional: boolean,
  ariaLabel: string | undefined | null,
  label: string | undefined,
): string | undefined {
  if (!optional) {
    return ariaLabel ?? undefined;
  }
  if (label?.trim()) {
    return undefined;
  }
  const base = ariaLabel?.trim();
  if (!base) {
    return undefined;
  }
  if (OPTIONAL_MARKED_IN_LABEL.test(base)) {
    return base;
  }
  return `${base} (optional)`;
}
